import React from "react";
import Typography from "@mui/material/Typography";
import type { TypographyProps } from "@mui/material/Typography";

/**
 * Splits a formatted amount so the cents can sit raised. The separator stays
 * with the cents rather than being dropped: the text content has to still read
 * as money to a screen reader and to a copy-paste.
 */
const CENTS = /^(.*)(\.\d{2})$/;

interface BalanceAmountProps {
  /** Already formatted and localised, e.g. "$14,448.27". */
  value: string;
  /** Scale of the whole figure. */
  variant?: TypographyProps["variant"];
  /**
   * Size of the raised cents. The xs step reads right against the row's h6,
   * but it is a fixed value rather than a proportional one, so a larger
   * figure has to say what its cents should be.
   */
  centsSize?: string;
}

/**
 * An amount with its cents raised. `formatCashFull` always formats through the
 * `en-US` grouping, so the separator is a period; anything that does not match
 * renders whole rather than guessing where the cents are.
 */
const BalanceAmount: React.FC<BalanceAmountProps> = ({
  value,
  variant = "h6",
  centsSize = "var(--type-size-xs)",
}) => {
  const parts = CENTS.exec(value);

  if (!parts) return <Typography variant={variant}>{value}</Typography>;

  return (
    <Typography variant={variant} sx={{ whiteSpace: "nowrap" }}>
      {parts[1]}
      <Typography
        component="sup"
        variant={variant}
        sx={{
          marginLeft: "1px",
          fontSize: centsSize,
        }}
      >
        {parts[2]}
      </Typography>
    </Typography>
  );
};

export default BalanceAmount;
